import { useState } from 'react'
import { useListState } from '../hooks/useListState'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { modulesApi, type ModuleSummary, type ModuleInstallation } from '../api/client'
import { Badge, Card, EmptyState, Input, PageHeader } from '../components/ui'

function formatDate(s?: string) {
  if (!s) return '-'
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return '-'
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

function installMethod(inst: ModuleInstallation): 'github' | 'upload' {
  return inst.source_repo_url || inst.source_url ? 'github' : 'upload'
}

function MethodBadge({ method, t }: { method: 'github' | 'upload'; t: (k: string) => string }) {
  if (method === 'github') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-ink-900 text-white text-xs font-medium">
        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 .3a12 12 0 00-3.8 23.4c.6.1.8-.3.8-.6v-2c-3.3.7-4-1.6-4-1.6-.5-1.4-1.3-1.7-1.3-1.7-1.1-.7.1-.7.1-.7 1.2.1 1.8 1.2 1.8 1.2 1.1 1.8 2.8 1.3 3.5 1 .1-.8.4-1.3.8-1.6-2.7-.3-5.5-1.3-5.5-5.9 0-1.3.5-2.4 1.2-3.2-.1-.3-.5-1.5.1-3.2 0 0 1-.3 3.3 1.2a11.5 11.5 0 016 0c2.3-1.5 3.3-1.2 3.3-1.2.6 1.7.2 2.9.1 3.2.7.8 1.2 1.9 1.2 3.2 0 4.6-2.8 5.6-5.5 5.9.4.4.8 1.1.8 2.2v3.3c0 .3.2.7.8.6A12 12 0 0012 .3"/>
        </svg>
        {t('modules.methodGithub')}
      </span>
    )
  }
  return <Badge tone="accent">{t('modules.methodUpload')}</Badge>
}

function StatusPill({ status }: { status: string }) {
  const tone =
    status === 'installed' ? 'success'
    : status === 'failed' || status === 'update_failed' ? 'danger'
    : 'neutral'
  return <Badge tone={tone}>{status}</Badge>
}

function InstallationsDrawer({
  name,
  onClose,
}: {
  name: string
  onClose: () => void
}) {
  const { t } = useTranslation()
  const { data, isLoading, error } = useQuery({
    queryKey: ['module-installations', name],
    queryFn: () => modulesApi.installations(name),
  })

  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  const [downloadError, setDownloadError] = useState<string | null>(null)

  const handleDownload = async (inst: ModuleInstallation) => {
    setDownloadError(null)
    setDownloadingId(inst.addon_id)
    try {
      await modulesApi.downloadArchive(inst.addon_id, inst.name)
    } catch {
      setDownloadError(t('modules.downloadFailed'))
    } finally {
      setDownloadingId(null)
    }
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      <aside className="fixed right-0 top-0 bottom-0 w-full max-w-2xl bg-white z-50 shadow-xl flex flex-col">
        <header className="flex items-start justify-between px-6 py-4 border-b border-gray-200">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wide text-gray-500">{t('modules.module')}</p>
            <h2 className="text-lg font-semibold text-gray-900 font-mono truncate">{name}</h2>
            {data && (
              <p className="text-sm text-gray-500 mt-0.5">
                {t('modules.installedOnCount', { count: data.length })}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-700 rounded-md"
            aria-label={t('common.closeMenu')}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </header>

        <div className="flex-1 overflow-auto px-6 py-4">
          {isLoading && <p className="text-sm text-gray-500">{t('common.loading')}</p>}
          {error && <p className="text-sm text-red-600">{t('modules.loadError')}</p>}
          {downloadError && (
            <div className="mb-3 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
              {downloadError}
            </div>
          )}
          {data && data.length === 0 && (
            <p className="text-sm text-gray-500">{t('modules.noInstallations')}</p>
          )}
          {data && data.length > 0 && (
            <ul className="space-y-3">
              {data.map((inst) => {
                const method = installMethod(inst)
                const sourceURL = inst.source_repo_url || inst.source_url
                return (
                  <li key={inst.addon_id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <Link
                          to={`/instances/${inst.instance_id}`}
                          className="text-sm font-semibold text-brand-700 hover:text-brand-800 truncate block"
                        >
                          {inst.subdomain}
                        </Link>
                        <p className="text-xs text-gray-500 truncate">{inst.instance_name}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <MethodBadge method={method} t={t} />
                        <StatusPill status={inst.status} />
                      </div>
                    </div>
                    <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                      <div>
                        <dt className="text-gray-500">{t('modules.owner')}</dt>
                        <dd className="text-gray-800 truncate">
                          {inst.owner_email ?? <span className="text-gray-400">-</span>}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">{t('modules.installedAt')}</dt>
                        <dd className="text-gray-800">{formatDate(inst.installed_at)}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">{t('modules.version')}</dt>
                        <dd className="text-gray-800 font-mono">{inst.version || '-'}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">{t('modules.author')}</dt>
                        <dd className="text-gray-800 truncate">{inst.author || '-'}</dd>
                      </div>
                      {sourceURL && (
                        <div className="col-span-2">
                          <dt className="text-gray-500">{t('modules.source')}</dt>
                          <dd className="text-gray-800 truncate">
                            <a
                              href={sourceURL}
                              target="_blank"
                              rel="noreferrer"
                              className="text-brand-700 hover:underline break-all"
                            >
                              {sourceURL}
                              {inst.source_ref ? ` @ ${inst.source_ref}` : ''}
                            </a>
                          </dd>
                        </div>
                      )}
                    </dl>
                    <div className="mt-3 flex justify-end">
                      <button
                        onClick={() => handleDownload(inst)}
                        disabled={!inst.has_stored_archive || downloadingId === inst.addon_id}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-gray-200 bg-white hover:bg-gray-50 text-xs font-medium text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        title={
                          !inst.has_stored_archive
                            ? t('modules.archiveUnavailable')
                            : t('modules.downloadArchive')
                        }
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                        </svg>
                        {downloadingId === inst.addon_id ? t('modules.downloading') : t('modules.downloadZip')}
                      </button>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </aside>
    </>
  )
}

export default function ModulesPage() {
  const { t } = useTranslation()
  const [activeName, setActiveName] = useState<string | null>(null)
  // Only the search half of useListState is used here — the module list is not
  // sorted or paginated.
  const { search, setSearch, debouncedSearch } = useListState({ sortKey: 'name' })

  const { data, isLoading, error } = useQuery({
    queryKey: ['modules', debouncedSearch],
    queryFn: () => modulesApi.list(debouncedSearch || undefined),
  })

  const modules: ModuleSummary[] = data ?? []

  return (
    <div className="p-4 sm:p-8">
      <PageHeader title={t('modules.title')} description={t('modules.subtitle')} />

      <div className="mb-4">
        <Input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('modules.searchPlaceholder')}
          aria-label={t('modules.searchPlaceholder')}
          className="max-w-md"
        />
      </div>

      {isLoading && <p className="text-sm text-gray-500">{t('common.loading')}</p>}
      {error && <p className="text-sm text-red-600">{t('modules.loadError')}</p>}
      {!isLoading && !error && modules.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-200">
          <EmptyState title={t('modules.empty')} />
        </div>
      )}

      {modules.length > 0 && (
        <Card padded={false} className="overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left">
                <th className="px-4 py-2.5 font-medium text-gray-600">{t('modules.colName')}</th>
                <th className="px-4 py-2.5 font-medium text-gray-600">{t('modules.colInstalls')}</th>
                <th className="px-4 py-2.5 font-medium text-gray-600">{t('modules.colMethod')}</th>
                <th className="px-4 py-2.5 font-medium text-gray-600">{t('modules.colVersion')}</th>
                <th className="px-4 py-2.5 font-medium text-gray-600">{t('modules.colAuthors')}</th>
                <th className="px-4 py-2.5 font-medium text-gray-600">{t('modules.colLastInstalled')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {modules.map((m) => (
                <tr
                  key={m.name}
                  onClick={() => setActiveName(m.name)}
                  className="hover:bg-gray-50 cursor-pointer"
                >
                  <td className="px-4 py-2.5 font-mono text-gray-900">{m.name}</td>
                  <td className="px-4 py-2.5 text-gray-700">{m.install_count}</td>
                  <td className="px-4 py-2.5">
                    <span className="inline-flex items-center gap-1.5 text-xs text-gray-600">
                      {m.github_count > 0 && (
                        <span>
                          {m.github_count} {t('modules.methodGithub')}
                        </span>
                      )}
                      {m.github_count > 0 && m.upload_count > 0 && <span>·</span>}
                      {m.upload_count > 0 && (
                        <span>
                          {m.upload_count} {t('modules.methodUpload')}
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-gray-700">
                    {m.latest_version || '-'}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-700 truncate max-w-[200px]">
                    {m.authors.length > 0 ? m.authors.join(', ') : '-'}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">
                    {formatDate(m.last_installed_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </Card>
      )}

      {activeName && <InstallationsDrawer name={activeName} onClose={() => setActiveName(null)} />}
    </div>
  )
}
